import { Card, CardMedia, Container, Link, Typography, Box, CircularProgress } from '@mui/material';
import ApiContext from 'contexts/apiContext';
import { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useMatch } from 'react-router-dom';

import banner from 'static/banner.png';
import logoCircleGreen from 'static/logo-circle-green.png';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  card: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
  },
  media: {
    height: '14vh',
    objectFit: 'contain',
  },
  loaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '50vh',
  },
  logo: {
    height: '20vh',
    objectFit: 'contain',
    marginBottom: theme.spacing(4),
    borderRadius: '50%',
  },
}));

export default function Auth() {
  const { currentUser } = useContext(ApiContext);
  const { classes } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  // Check if we're on the impersonate route using a precise match
  const isImpersonateRoute = useMatch('/auth/impersonate/*');

  // Give Firebase a moment to determine auth state
  useEffect(() => {
    if (currentUser !== undefined) {
      setHasCheckedAuth(true);
    }
  }, [currentUser]);

  useEffect(() => {
    // Don't redirect when on impersonate route - let ImpersonateScreen handle store switch and sign-in
    if (currentUser && !isImpersonateRoute) {
      // Redirect to the original destination or default to /app/home
      const state = location.state;
      const redirectPath =
        state && typeof state === 'object' && 'from' in state && typeof state.from === 'string' && state.from.trim() !== ''
          ? state.from
          : '/app/home';

      navigate(redirectPath, { replace: true });
    }
  }, [currentUser, navigate, location.state, isImpersonateRoute]);

  // Show loader while checking authentication or if we have a current user (redirecting)
  // When on impersonate route, render ImpersonateScreen even if logged in so it can switch stores
  if (!hasCheckedAuth || (currentUser && !isImpersonateRoute)) {
    return (
      <Container className={classes.container} maxWidth='sm'>
        <Box className={classes.loaderContainer}>
          <CardMedia component='img' className={classes.logo} image={logoCircleGreen} title='Logo Buen Provecho' />
          <CircularProgress color='primary' size='3rem' />
        </Box>
      </Container>
    );
  }

  // Show login screen when user is not authenticated
  return (
    <Container className={classes.container} maxWidth='sm'>
      <CardMedia component='img' className={classes.media} image={banner} title='Logo Buen Provecho' />
      <Typography variant='subtitle2' align='center' sx={{ mt: 4 }}>
        ¡Bienvenid@ a la aplicación para tiendas de Buen Provecho! 🫶🏼
      </Typography>
      <Typography variant='subtitle2' align='center'>
        Si quieres adherir tu tienda a la plataforma, llena el{' '}
        <Link href='https://www.buenprovechoapp.com/#registra-tu-tienda' target='_blank'>
          formulario de registro
        </Link>
        .
      </Typography>
      <Card className={classes.card}>
        <Outlet />
      </Card>
    </Container>
  );
}
